'use client';

import { useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import { Search, LayoutGrid, List, Plus } from 'lucide-react';
import AuthGuard from '@/components/AuthGuard';
import { Avatar, Button as DsButton } from '@/components/ds';
import { useAuthContext } from '@/components/providers/AuthProvider';
import { cn } from '@/lib/utils';
import GroupCard, { type GroupCardData } from '@/components/groups/GroupCard';
import { SectionHeader } from '@/app/(authed)/home/_home/SectionHeader';

type SortKey = 'active' | 'newest' | 'popular' | 'az';
type Privacy = 'all' | 'public' | 'request' | 'private';
type Tab = 'my' | 'discover';
type ViewMode = 'grid' | 'list';

const VIEW_STORAGE_KEY = 'indiestry.groups.view';

type ApiGroup = {
  id: string;
  name: string;
  description: string | null;
  tags: string[];
  memberCount: number;
  isJoined: boolean;
  isAdmin: boolean;
  ownerId: string;
  createdAt: string;
  privacy: string;
  lastMessageAt?: string | null;
  activityScore?: number | null;
  banner?: string | null;
  viewerRequestStatus?: string | null;
  pendingRequestCount?: number;
};

function toCard(g: ApiGroup, currentUserId?: string | null): GroupCardData {
  const privacy: GroupCardData['privacy'] =
    g.privacy === 'private' ? 'private' : g.privacy === 'request' ? 'request' : 'public';
  return {
    id: g.id,
    name: g.name,
    description: g.description,
    tags: Array.isArray(g.tags) ? g.tags : [],
    privacy,
    memberCount: g.memberCount,
    channelCount: 1,
    isJoined: !!g.isJoined,
    isOwner: !!currentUserId && g.ownerId === currentUserId,
    lastMessageAt: g.lastMessageAt ?? null,
    banner: g.banner ?? 'default',
    requestStatus: (g.viewerRequestStatus as GroupCardData['requestStatus']) ?? 'none',
  };
}

function HubInner() {
  const { user } = useAuthContext();
  const [tab, setTab] = useState<Tab>('discover');
  const [sort, setSort] = useState<SortKey>('active');
  const [privacy, setPrivacy] = useState<Privacy>('all');
  const [tag, setTag] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [view, setView] = useState<ViewMode>('grid');
  const [groups, setGroups] = useState<ApiGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [joiningId, setJoiningId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  // Load saved view preference once.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const saved = window.localStorage.getItem(VIEW_STORAGE_KEY);
    if (saved === 'grid' || saved === 'list') setView(saved);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(VIEW_STORAGE_KEY, view);
  }, [view]);

  // Fetch when filters change. Server applies sort/filter; client renders.
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        setLoading(true);
        setError(null);
        const params = new URLSearchParams();
        if (user?.id) params.set('userId', user.id);
        params.set('tab', tab);
        params.set('sort', sort);
        params.set('privacy', privacy);
        if (tag) params.set('tag', tag);
        if (query.trim()) params.set('q', query.trim());
        const res = await fetch(`/api/groups?${params.toString()}`, { cache: 'no-store' });
        if (!res.ok) throw new Error(`Request failed (${res.status})`);
        const data = (await res.json()) as ApiGroup[];
        if (!cancelled) setGroups(Array.isArray(data) ? data : []);
      } catch (err) {
        if (!cancelled) {
          console.error('[Hub] load failed:', err);
          setError(err instanceof Error ? err.message : 'Failed to load groups');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [user?.id, tab, sort, privacy, tag, query]);

  const myCount = useMemo(
    () => groups.filter((g) => g.isJoined || g.ownerId === user?.id).length,
    [groups, user?.id],
  );

  // Top 8 tags from current result set, most-frequent first.
  const tagOptions = useMemo(() => {
    const freq = new Map<string, number>();
    for (const g of groups) {
      for (const t of g.tags || []) {
        const k = String(t).trim();
        if (!k) continue;
        freq.set(k, (freq.get(k) || 0) + 1);
      }
    }
    return Array.from(freq.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)
      .map(([t]) => t);
  }, [groups]);

  const anyFilterActive =
    privacy !== 'all' || !!tag || query.trim().length > 0 || sort !== 'active';

  const clearFilters = () => {
    setPrivacy('all');
    setTag(null);
    setQuery('');
    setSort('active');
  };

  const hero = useMemo(() => {
    if (tab !== 'discover') return null;
    return groups[0] || null;
  }, [tab, groups]);

  // Public join is one-click and optimistic (no confirmation dialog). Flip to
  // member state immediately; roll back + surface an inline error on failure.
  const handleJoin = async (id: string) => {
    if (!user) return;
    const snapshot = groups;
    setActionError(null);
    setJoiningId(id);
    setGroups((prev) =>
      prev.map((g) =>
        g.id === id ? { ...g, isJoined: true, memberCount: g.memberCount + 1 } : g,
      ),
    );
    try {
      const res = await fetch(`/api/groups/${id}/join`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-user-id': user.id },
      });
      if (!res.ok) throw new Error('Join failed');
    } catch (err) {
      console.error('[Hub] join failed:', err);
      setGroups(snapshot); // rollback
      setActionError('Could not join the group. Please try again.');
    } finally {
      setJoiningId(null);
    }
  };

  // Request to join (request-privacy groups) — optimistic pending, rollback on
  // failure. Actor identity comes from the session cookie (no x-user-id).
  const handleRequest = async (id: string) => {
    if (!user) return;
    const snapshot = groups;
    setActionError(null);
    setGroups((prev) =>
      prev.map((g) => (g.id === id ? { ...g, viewerRequestStatus: 'pending' } : g)),
    );
    try {
      const res = await fetch(`/api/groups/${id}/requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      if (!res.ok) throw new Error('Request failed');
    } catch (err) {
      console.error('[Hub] request failed:', err);
      setGroups(snapshot);
      setActionError('Could not send your request. Please try again.');
    }
  };

  const handleCancelRequest = async (id: string) => {
    if (!user) return;
    const snapshot = groups;
    setActionError(null);
    setGroups((prev) =>
      prev.map((g) => (g.id === id ? { ...g, viewerRequestStatus: 'none' } : g)),
    );
    try {
      const res = await fetch(`/api/groups/${id}/requests`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Cancel failed');
    } catch (err) {
      console.error('[Hub] cancel request failed:', err);
      setGroups(snapshot);
      setActionError('Could not cancel your request. Please try again.');
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-text-primary tracking-tight">Groups</h1>
          <p className="text-sm text-text-secondary mt-1 max-w-xl">
            Side conversations, practice rooms, and communities for voice actors.
          </p>
        </div>
        <Link href="/groups/create">
          <DsButton variant="primary" size="md">
            <Plus className="w-4 h-4" strokeWidth={1.75} />
            Create group
          </DsButton>
        </Link>
      </div>

      {/* Tabs */}
      <div role="tablist" className="border-b border-border-subtle flex gap-5 mb-4">
        <TabBtn active={tab === 'my'} onClick={() => setTab('my')}>
          My groups
          <span
            className={cn(
              'ml-2 inline-flex items-center px-1.5 py-0 rounded-full text-[10px] font-mono',
              tab === 'my'
                ? 'bg-accent-soft text-accent'
                : 'bg-surface-3 text-text-tertiary',
            )}
          >
            {myCount}
          </span>
        </TabBtn>
        <TabBtn active={tab === 'discover'} onClick={() => setTab('discover')}>
          Discover
        </TabBtn>
      </div>

      {/* Discover hero — banner-tinted strip (data-banner + --hdr-bg, like the
          card band / detail header), BannerAvatar on the LEFT, no stray tile.
          Text uses theme tokens so it reads on the wash in both themes. */}
      {tab === 'discover' && hero && (
        <div
          data-banner={hero.banner || 'default'}
          className="rounded-md p-5 mb-4 relative overflow-hidden border border-border-subtle flex items-start gap-4"
          style={{ background: 'var(--hdr-bg)' }}
        >
          <Avatar square ring name={hero.name} size={56} className="flex-shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="text-[11px] uppercase tracking-wider font-mono text-accent mb-1">
              Trending this week
            </p>
            <h2 className="text-lg font-semibold text-text-primary truncate">{hero.name}</h2>
            <p className="text-sm text-text-secondary mt-1 line-clamp-2 max-w-2xl">
              {hero.description || 'No description.'}
            </p>
            <div className="mt-3">
              <Link href={`/groups/${hero.id}`}>
                <DsButton variant="primary" size="sm">
                  Take a look
                </DsButton>
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Filter bar */}
      <div className="rounded-md border border-border-subtle bg-surface-1 p-3 mb-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <Search
              className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-text-tertiary pointer-events-none"
              strokeWidth={1.75}
            />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search groups by name or tag"
              aria-label="Search groups"
              className="w-full h-9 pl-8 pr-2 rounded-sm bg-surface-2 border border-border-subtle text-sm text-text-primary placeholder-text-tertiary focus:outline-none focus:border-accent transition-colors"
            />
          </div>

          <div className="flex items-center rounded-sm border border-border-subtle overflow-hidden">
            {(['active', 'newest', 'popular', 'az'] as SortKey[]).map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setSort(s)}
                className={cn(
                  'px-3 h-9 text-xs transition-colors capitalize',
                  sort === s
                    ? 'bg-accent-soft text-accent'
                    : 'text-text-secondary hover:text-text-primary hover:bg-surface-2',
                )}
                aria-pressed={sort === s}
              >
                {s === 'az' ? 'A–Z' : s}
              </button>
            ))}
          </div>

          <div className="flex items-center rounded-sm border border-border-subtle overflow-hidden">
            <button
              type="button"
              onClick={() => setView('grid')}
              aria-label="Grid view"
              aria-pressed={view === 'grid'}
              className={cn(
                'w-9 h-9 inline-flex items-center justify-center transition-colors',
                view === 'grid'
                  ? 'bg-accent-soft text-accent'
                  : 'text-text-secondary hover:text-text-primary hover:bg-surface-2',
              )}
            >
              <LayoutGrid className="w-4 h-4" strokeWidth={1.75} />
            </button>
            <button
              type="button"
              onClick={() => setView('list')}
              aria-label="List view"
              aria-pressed={view === 'list'}
              className={cn(
                'w-9 h-9 inline-flex items-center justify-center transition-colors',
                view === 'list'
                  ? 'bg-accent-soft text-accent'
                  : 'text-text-secondary hover:text-text-primary hover:bg-surface-2',
              )}
            >
              <List className="w-4 h-4" strokeWidth={1.75} />
            </button>
          </div>
        </div>

        {/* Chips row */}
        <div className="mt-3 pt-3 border-t border-border-subtle flex flex-wrap items-center gap-2">
          {(['all', 'public', 'request', 'private'] as Privacy[]).map((p) => (
            <Chip
              key={p}
              active={privacy === p}
              onClick={() => setPrivacy(p)}
              label={
                p === 'all'
                  ? 'All'
                  : p === 'public'
                  ? 'Public'
                  : p === 'request'
                  ? 'Request'
                  : 'Private'
              }
            />
          ))}
          {tagOptions.length > 0 && (
            <span className="w-px h-5 bg-border-subtle mx-1" aria-hidden />
          )}
          {tagOptions.length > 0 && (
            <Chip active={tag === null} onClick={() => setTag(null)} label="Any" />
          )}
          {tagOptions.map((t) => (
            <Chip
              key={t}
              active={tag === t}
              onClick={() => setTag(t === tag ? null : t)}
              label={`#${t}`}
            />
          ))}
        </div>
      </div>

      {/* Section header above the all-groups grid — matches the home
          page kicker + H2 pattern. The result count moves into the
          kicker; clearing filters stays inline on the right. */}
      <div className="flex items-end justify-between gap-3 mb-3">
        <SectionHeader
          kicker={
            loading
              ? 'Loading…'
              : anyFilterActive
              ? `Results · ${groups.length}`
              : `All groups · ${groups.length}`
          }
          title={anyFilterActive ? 'Filtered groups' : 'Browse the community'}
        />
        {anyFilterActive && (
          <button
            type="button"
            onClick={clearFilters}
            className="text-xs text-accent hover:text-accent-hover whitespace-nowrap"
          >
            Clear filters
          </button>
        )}
      </div>

      {/* Results */}
      {error && (
        <div className="text-sm text-status-busy mb-3" role="alert">
          {error}
        </div>
      )}

      {actionError && (
        <div
          className="text-sm text-status-busy mb-3 px-3 py-2 rounded-sm bg-status-busy/10 border border-status-busy/30"
          role="alert"
          aria-live="polite"
        >
          {actionError}
        </div>
      )}

      {!loading && groups.length === 0 && !error && (
        <div className="rounded-md border border-border-subtle bg-surface-1 p-10 text-center">
          <p className="text-sm text-text-secondary">
            No groups match these filters yet.{' '}
            <Link href="/groups/create" className="text-accent hover:text-accent-hover">
              Create one
            </Link>
            ?
          </p>
        </div>
      )}

      {view === 'grid' ? (
        <div
          className="grid gap-4"
          style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))' }}
        >
          {groups.map((g) => (
            <GroupCard
              key={g.id}
              group={toCard(g, user?.id)}
              onJoin={handleJoin}
              onRequest={handleRequest}
              onCancelRequest={handleCancelRequest}
            />
          ))}
        </div>
      ) : (
        <div className="rounded-md border border-border-subtle bg-surface-1 overflow-hidden">
          {groups.map((g, i) => (
            <div
              key={g.id}
              className={cn(i > 0 && 'border-t border-border-subtle')}
            >
              <GroupCard
                group={toCard(g, user?.id)}
                onJoin={handleJoin}
                onRequest={handleRequest}
                onCancelRequest={handleCancelRequest}
                layout="list"
              />
            </div>
          ))}
        </div>
      )}

      {joiningId && (
        <span className="sr-only" role="status" aria-live="polite">
          Joining group…
        </span>
      )}
    </div>
  );
}

function TabBtn({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={cn(
        'pb-2 text-sm font-medium transition-colors -mb-px border-b-2',
        active
          ? 'text-text-primary border-accent'
          : 'text-text-secondary border-transparent hover:text-text-primary',
      )}
    >
      {children}
    </button>
  );
}

function Chip({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        'inline-flex items-center px-2.5 h-7 rounded-sm text-xs transition-colors border',
        active
          ? 'bg-accent-soft text-accent border-transparent'
          : 'bg-transparent text-text-secondary border-border-subtle hover:text-text-primary hover:bg-surface-2',
      )}
    >
      {label}
    </button>
  );
}

export default function GroupsHubPage() {
  return (
    <AuthGuard>
      <HubInner />
    </AuthGuard>
  );
}
